import './bwa.css';
import { useRef, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { Dialog } from '../../components/Dialog';
import { monthName, MONTHS, today } from '../../lib/format';
import { eurS, parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import type { BwaSuggestion, KanzleiBwa } from './types';
import { KiHinweis } from '../KiHinweis';

// BWA hochladen: Datei, Jahr, bis Monat und je Monat Umsatz und vorläufiges Ergebnis aus der BWA. Ist die KI
// eingerichtet, liest sie die BWA gleich nach dem Auswählen aus und füllt alles vor (gelb markiert); die Kanzlei
// vergleicht und lädt dann hoch. Enthält die BWA nicht für jeden Monat Einzelwerte, kommt die Summe seit Januar dazu.
// Monate aus der bisherigen BWA des Jahres sind vorausgefüllt.

const num = (v: number) => v.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const span = (toMonth: number) => (toMonth === 1 ? 'Januar' : `Januar–${monthName(toMonth)}`);

export function BwaDialog({
  clientId,
  clientName,
  reports,
  ki,
  kiAus,
  kiTest,
  onClose,
}: {
  clientId: string;
  clientName: string;
  reports: KanzleiBwa[];
  ki: boolean;
  kiAus: boolean;
  kiTest: boolean;
  onClose(): void;
}) {
  const toast = useToast();
  const t = today();
  // Standard: Vormonat (die BWA kommt nach dem Monatsende)
  const [year, setYear] = useState(t.month === 1 ? t.year - 1 : t.year);
  const [toMonth, setToMonth] = useState(t.month === 1 ? 12 : t.month - 1);
  const [file, setFile] = useState<File | null>(null);
  // Eingaben je „Jahr-Monat-Feld“; ohne Eingabe gilt der Wert der bisherigen BWA des Jahres
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [sum, setSum] = useState({ revenue: '', result: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // KI: Stand des Auslesens, Felder mit Werten der KI (bis sie geändert werden) und die Summe laut BWA zum Abgleich
  const [kiState, setKiState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [kiNote, setKiNote] = useState<string | null>(null);
  const [kiFields, setKiFields] = useState<Set<string>>(new Set());
  const [kiSum, setKiSum] = useState<{ year: number; toMonth: number; revenue: number; result: number } | null>(null);
  const kiRun = useRef(0);

  const years = [t.year, t.year - 1, t.year - 2];
  const lastMonth = (y: number) => (y === t.year ? t.month - 1 : 12);
  const prev = reports.find((r) => r.current && r.year === year);
  const value = (m: number, f: 'revenue' | 'result') => {
    const k = `${year}-${m}-${f}`;
    if (k in typed) return typed[k]!;
    const p = prev?.months[m - 1];
    return p ? num(p[f]) : '';
  };
  const set = (m: number, f: 'revenue' | 'result', v: string) => {
    setTyped((x) => ({ ...x, [`${year}-${m}-${f}`]: v }));
    unmark(`${year}-${m}-${f}`);
    setError(null);
  };
  const unmark = (k: string) =>
    setKiFields((x) => {
      if (!x.has(k)) return x;
      const n = new Set(x);
      n.delete(k);
      return n;
    });
  const kiCls = (k: string) => (kiFields.has(k) ? 'ki' : undefined);

  // Gleich nach dem Auswählen: KI liest die BWA aus und füllt Jahr, Monat, Monatswerte und Summe vor
  const readKi = async (f: File) => {
    const run = ++kiRun.current;
    setKiState('busy');
    setKiNote(null);
    const fd = new FormData();
    fd.append('file', f, f.name);
    try {
      const s = await api.post<BwaSuggestion>(`/api/kanzlei/bwa/${clientId}/ki`, fd);
      if (run !== kiRun.current) return;
      if (!s.found) {
        setKiState('error');
        setKiNote(s.hinweis);
        return;
      }
      const marks = new Set<string>();
      const values: Record<string, string> = {};
      for (const m of s.months) {
        for (const fld of ['revenue', 'result'] as const) {
          values[`${s.year}-${m.month}-${fld}`] = num(m[fld]);
          marks.add(`${s.year}-${m.month}-${fld}`);
        }
      }
      setYear(s.year);
      setToMonth(s.toMonth);
      setTyped((x) => ({ ...x, ...values }));
      if (s.sum) {
        setSum({ revenue: num(s.sum.revenue), result: num(s.sum.result) });
        marks.add('sum-revenue').add('sum-result');
      }
      setKiSum(s.sum ? { year: s.year, toMonth: s.toMonth, ...s.sum } : null);
      setKiFields(marks);
      // Jahr und Monat erkannt, aber keine Zahlen: wie ein Fehler anzeigen (hinweis sagt, was zu tun ist)
      setKiState(marks.size ? 'done' : 'error');
      setKiNote(s.hinweis);
      setError(null);
    } catch (err) {
      if (run !== kiRun.current) return;
      setKiState('error');
      setKiNote(errorText(err));
    }
  };

  // Eingaben prüfen: je Monat beides oder nichts
  const rows = Array.from({ length: toMonth }, (_, i) => {
    const m = i + 1;
    const r = value(m, 'revenue').trim();
    const e = value(m, 'result').trim();
    return { m, r, e, revenue: parseEuro(r), result: parseEuro(e) };
  });
  const filled = rows.filter((x) => x.r || x.e);
  const complete = filled.length === toMonth;
  const sumRevenue = parseEuro(sum.revenue);
  const sumResult = parseEuro(sum.result);
  const preview = complete
    ? rows.every((x) => typeof x.result === 'number')
      ? rows.reduce((a, x) => a + (x.result as number), 0)
      : null
    : typeof sumResult === 'number'
      ? sumResult
      : null;

  // Summe laut BWA passt nicht zu den Monaten (z. B. Vormonate in der neuen BWA berichtigt): Hinweis zum Prüfen
  const kiMismatch =
    kiState === 'done' && kiSum && kiSum.year === year && kiSum.toMonth === toMonth && complete && preview !== null
      ? Math.abs(preview - kiSum.result) >= 1 ||
        Math.abs(rows.reduce((a, x) => a + (typeof x.revenue === 'number' ? x.revenue : 0), 0) - kiSum.revenue) >= 1
      : false;

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!file) return setError('Bitte die BWA auswählen (PDF oder Foto).');
    for (const x of filled) {
      if (!x.r || !x.e) return setError(`${monthName(x.m)}: Bitte Umsatz und Ergebnis eintragen oder beide leer lassen.`);
      if (typeof x.revenue !== 'number' || typeof x.result !== 'number') {
        return setError(`${monthName(x.m)}: Bitte gültige Beträge eintragen, z. B. 21.400 oder −1.900.`);
      }
    }
    if (!complete && (typeof sumRevenue !== 'number' || typeof sumResult !== 'number')) {
      return setError(`Bitte die Summe ${span(toMonth)} aus der BWA eintragen (Umsatz und Ergebnis).`);
    }
    setError(null);
    setBusy(true);
    const fd = new FormData();
    // zuerst die Angaben, dann die Datei (so prüft der Server, bevor er speichert)
    fd.append(
      'data',
      JSON.stringify({
        year,
        toMonth,
        months: filled.map((x) => ({ month: x.m, revenue: x.revenue, result: x.result })),
        sum: complete ? null : { revenue: sumRevenue, result: sumResult },
      }),
    );
    fd.append('file', file, file.name);
    try {
      await api.post(`/api/kanzlei/bwa/${clientId}`, fd);
      toast(
        `BWA ${monthName(toMonth)} ${year} hochgeladen${preview !== null ? ` · Ergebnis ${span(toMonth)}: ${eurS(preview)}` : ''}`,
      );
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} title="BWA hochladen" className="bwadlg">
      <p className="note">{clientName}</p>
      <form className="form" onSubmit={submit} noValidate>
        <label>
          BWA (PDF oder Foto) *
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              setError(null);
              if (f && ki) void readKi(f);
            }}
          />
        </label>
        {ki && <KiHinweis test={kiTest}>Die KI (über Google Cloud, Rechenzentren in der EU) liest die BWA aus.</KiHinweis>}
        {ki ? (
          kiState !== 'idle' && (
            <p className={`note bwa-ki ${kiState}`} role="status">
              {kiState === 'busy' && (
                <>
                  <span className="spinner sm" aria-hidden="true" /> Die KI liest die BWA …
                </>
              )}
              {kiState === 'done' &&
                `✓ Von der KI ausgelesen (gelb). Bitte kurz mit der BWA vergleichen.${kiNote ? ` ${kiNote}` : ''}`}
              {kiState === 'error' && (kiNote ?? 'Die KI konnte die BWA nicht lesen. Bitte von Hand eintragen.')}
            </p>
          )
        ) : kiAus ? (
          <p className="note">Die KI ist für diesen Mandanten ausgeschaltet. Bitte die Zahlen von Hand eintragen.</p>
        ) : (
          <p className="note">Automatisch auslesen geht, sobald die KI eingerichtet ist.</p>
        )}
        <div className="row bwa-two">
          <label>
            Jahr *
            <select
              value={year}
              onChange={(e) => {
                const y = Number(e.target.value);
                setYear(y);
                setToMonth((m) => Math.min(m, lastMonth(y)));
                setError(null);
              }}
            >
              {years
                .filter((y) => lastMonth(y) >= 1)
                .map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Bis Monat *
            <select
              value={toMonth}
              onChange={(e) => {
                setToMonth(Number(e.target.value));
                setError(null);
              }}
            >
              {MONTHS.slice(0, lastMonth(year)).map((n, i) => (
                <option key={n} value={i + 1}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="tw">
          <table className="bwa-in">
            <thead>
              <tr>
                <th>Monat</th>
                <th>Umsatz (€)</th>
                <th>Ergebnis (€)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((x) => (
                <tr key={x.m}>
                  <td>{monthName(x.m)}</td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={x.r}
                      className={kiCls(`${year}-${x.m}-revenue`)}
                      aria-label={`Umsatz ${monthName(x.m)}`}
                      onChange={(e) => set(x.m, 'revenue', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={x.e}
                      className={kiCls(`${year}-${x.m}-result`)}
                      aria-label={`Ergebnis ${monthName(x.m)}`}
                      onChange={(e) => set(x.m, 'result', e.target.value)}
                    />
                  </td>
                </tr>
              ))}
              {!complete && (
                <tr className="bwa-sum">
                  <td>
                    <b>Summe {span(toMonth)} *</b>
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={sum.revenue}
                      className={kiCls('sum-revenue')}
                      aria-label={`Umsatz Summe ${span(toMonth)}`}
                      onChange={(e) => {
                        setSum((s) => ({ ...s, revenue: e.target.value }));
                        unmark('sum-revenue');
                        setError(null);
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={sum.result}
                      className={kiCls('sum-result')}
                      aria-label={`Ergebnis Summe ${span(toMonth)}`}
                      onChange={(e) => {
                        setSum((s) => ({ ...s, result: e.target.value }));
                        unmark('sum-result');
                        setError(null);
                      }}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="note">
          Aus der BWA: Umsatzerlöse und vorläufiges Ergebnis, Verlust mit Minus (z. B. −1.900). Monate ohne Einzelwerte leer
          lassen, dann die Summe seit Januar eintragen.
          {prev && ` Vorausgefüllt aus der ${prev.title}.`}
          {preview !== null && ` Der Mandant sieht: Ergebnis ${span(toMonth)} ${eurS(preview)}.`}
        </p>
        {kiMismatch && kiSum && (
          <p className="note bwa-warn" role="status">
            Achtung: Laut BWA ist die Summe {span(toMonth)} Umsatz {eurS(kiSum.revenue)}, Ergebnis {eurS(kiSum.result)}. Das passt
            nicht zu den Monaten oben (vielleicht wurden Vormonate berichtigt). Bitte die Vormonate mit der BWA vergleichen.
          </p>
        )}
        {error && (
          <p className="note bwa-err" role="alert">
            {error}
          </p>
        )}
        <div className="dlg-actions">
          <button type="submit" className="btn dark" disabled={busy || kiState === 'busy'}>
            Hochladen
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
        </div>
        <p className="note">
          {clientName} bekommt eine Benachrichtigung und sieht in der App Gewinn und Verlust Monat für Monat und die BWA selbst.
        </p>
      </form>
    </Dialog>
  );
}
